import type { Theme } from '@mui/material/styles';

import MenuIcon from '@mui/icons-material/Menu';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { useState } from 'react';

import { SideDrawer } from './SideDrawer';
import { useFooterLinks } from './footer/hooks/usefooterLinks';

import { VersionLabel } from '@egr/xbox/utils/Version';
import { makeStyles } from '@egr/xbox/utils/tss-react';

const useStyles = makeStyles({ name: 'MenuIconWithDrawer' })((theme: Theme) => ({
    list: {
        paddingTop: 0,
    },
    listItem: {
        paddingLeft: theme.spacing(1),
    },
    listItemText: {
        color: theme.palette.primary.main,
        fontSize: '14px',
    },
    footerContainer: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        padding: '16px',
        borderTop: '1px solid #0000001f',
        display: 'flex',
        justifyContent: 'center',
    },
    footer: {
        width: '67vw',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '15px',
    },
    footerLink: {
        fontSize: '11px',
        color: '#21212180',
        textDecoration: 'none',
    },
    versionText: {
        fontSize: '11px',
        color: '#21212180',
    },
}));

export const MenuIconWithDrawer: React.FC = () => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const toggleDrawer = (open: boolean) => () => setDrawerOpen(open);
    const { classes } = useStyles();
    const links = useFooterLinks();
    const footerLinks = links.filter((link) => link.id !== 'mainLink');
    const mainLink = links.find((link) => link.id === 'mainLink');

    return (
        <>
            <IconButton
                onClick={toggleDrawer(true)}
                aria-label="menu"
            >
                <MenuIcon />
            </IconButton>

            <SideDrawer
                open={drawerOpen}
                onClose={toggleDrawer(false)}
                anchor="right"
            >
                <List className={classes.list}>
                    {footerLinks.map((link) => (
                        <ListItem
                            key={link.id}
                            component="a"
                            href={link.href}
                            onClick={(e) => {
                                link.onClick?.(e);
                                setDrawerOpen(false);
                            }}
                            target={link.href.startsWith('http') ? '_blank' : undefined}
                            rel={link.href.startsWith('http') ? 'noreferrer' : undefined}
                            className={classes.listItem}
                        >
                            <ListItemText
                                primary={link.label}
                                primaryTypographyProps={{ className: classes.listItemText }}
                            />
                        </ListItem>
                    ))}
                </List>

                <Box className={classes.footerContainer}>
                    <Box className={classes.footer}>
                        <Typography
                            variant="body2"
                            component="a"
                            href={mainLink?.href}
                            target="_blank"
                            onClick={() => setDrawerOpen(false)}
                            className={classes.footerLink}
                        >
                            {mainLink?.label}
                        </Typography>
                        <Typography
                            variant="body2"
                            className={classes.versionText}
                        >
                            {VersionLabel}
                        </Typography>
                    </Box>
                </Box>
            </SideDrawer>
        </>
    );
};
